import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import Image from '@base/FutureImage';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Button from '@base/Button/Button';
import AquillaButton from 'aquilla/core/molecules/Button';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import {
  getDateMonthString,
  getTimeFormatInShortFormWithoutSecs
} from '@utils/dates';
import usePreciseTimer from '@hooks/usePreciseTimer';
import {
  DST_STATES,
  GOLD_CARD_MAX,
  GOLD_CARD_MIN,
  PURPLE_CARD_LIMIT
} from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/utils';
import Typography from 'aquilla/core/atoms/Typography';

const Container = styled.div`
  position: relative;
  padding: ${Spacings.SPACING_10B} 0;
  width: var(--layout-width);
  margin: auto;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
  }
`;

const Summary = styled.div`
  display: flex;
  padding-bottom: ${Spacings.SPACING_6B};
`;

const ContentDiv = styled.div`
  margin-right: ${Spacings.SPACING_10B};
  flex: 1;
`;

const HeaderWrapper = styled.div`
  margin-top: ${Spacings.SPACING_3B};
`;

const SubHeader = styled.div`
  font-size: ${Fonts.H5};
  line-height: 22px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const SubHeaderMweb = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.LIGHT};
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const Scholarship = styled.div`
  position: relative;
  margin-top: ${Spacings.SPACING_6B};
  height: ${({ isReattempt }) => (isReattempt ? '85px' : '78px')};
  display: flex;
  padding: ${({ isReattempt }) => (isReattempt ? '7px' : '0')}
    ${Spacings.SPACING_8B} 0
    ${({ isReattempt }) => (isReattempt ? '88px' : '144px')};
  align-items: center;
  background: ${({ backgroundImg }) => `url('${backgroundImg}')`};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const Details = styled.div`
  flex: 1;
`;

const StyAquillaButton = styled(AquillaButton)`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const MwebActionButtons = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_4B};
`;

const ReattemptTestContainer = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${Spacings.SPACING_6B};
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
`;

const Text = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H7};
  line-height: ${Fonts.H5};
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_6};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const Code = styled.div`
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Fonts.H3};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const StyledBtn = styled(Button)`
  width: 139px;
  height: 46px;
  margin-left: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H6};
`;

const ReattemptBtn = styled(StyledBtn)`
  &:hover {
    background-color: var(--color-base-1);
  }
`;

const TimeRemaining = styled.span`
  font-size: ${Fonts.H5};
  line-height: 22px;
  color: var(--color-i-red);
`;

const WebStyImage = styled(Image)`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const StyHeaderTypography = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const MWebStyImage = styled(Image)`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const ScholarshipMweb = styled.div`
  background: ${({ backgroundImg }) => `url('${backgroundImg}')`};
  position: relative;
  background-repeat: no-repeat;
  background-size: cover;
  height: ${Spacings.SPACING_8B};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MwebScholarshipBanner = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
  padding: ${Spacings.SPACING_4B};
  border: ${Spacings.SPACING_1} solid var(--color-i-divider);
  border-radius: ${Spacings.SPACING_2B};
`;

const ProofMarker = styled.div`
  position: absolute;
  width: 14px;
  aspect-ratio: 1;
  border-radius: 50%;
  border-top: 1px solid var(--color-i-white);
  border-right: 1px solid var(--color-i-white);
  background-color: var(--color-i-white);

  :nth-of-type(1) {
    top: 20%;
    left: 0;
    transform: translateX(-50%) rotate(45deg);
  }
  :nth-of-type(2) {
    bottom: 20%;
    left: 0;
    transform: translateX(-50%) rotate(45deg);
  }
  :nth-of-type(3) {
    top: 20%;
    right: 0;
    transform: translateX(50%) rotate(-45deg);
  }
  :nth-of-type(4) {
    bottom: 20%;
    right: 0;
    transform: translateX(50%) rotate(-45deg);
  }
`;

const getBackgroundImage = (discountPercentage) => {
  if (
    discountPercentage > GOLD_CARD_MIN &&
    discountPercentage <= GOLD_CARD_MAX
  ) {
    return '/static/images/dst/background-50-v2.svg';
  }
  if (discountPercentage > PURPLE_CARD_LIMIT) {
    return '/static/images/dst/background-30-v2.svg';
  }
  return '/static/images/dst/background-10-v2.svg';
};

const getMobBackgroundImage = (discountPercentage) => {
  if (
    discountPercentage > GOLD_CARD_MIN &&
    discountPercentage <= GOLD_CARD_MAX
  ) {
    return '/static/images/dst/dst_mob_gold.svg';
  }
  if (discountPercentage > PURPLE_CARD_LIMIT) {
    return '/static/images/dst/dst_mob_purple.svg';
  }
  return '/static/images/dst/dst_mob_silver.svg';
};

const TimeLeft = ({ timeRemaining }) => {
  const [time, setTime] = useState(timeRemaining);
  const handler = useCallback(() => {
    setTime((data) => data - 1);
  }, []);

  usePreciseTimer(handler, 1000, true);

  if (time < 60) return null;

  return (
    <TimeRemaining>
      Expires in {getTimeFormatInShortFormWithoutSecs(time, 'hr', 'min')}
    </TimeRemaining>
  );
};

const Result = ({ block: data, clickHandlers }) => {
  const { header, subHeader, ctaList } = data.extraBlockInfo;
  const {
    scholarshipCodeDetails: { code, discountPercentage, validTill },
    state,
    attemptsLeft
  } = data.data[0];
  const label = ctaList?.[0]?.label ?? '';
  const timeRemaining = Math.round(
    (new Date(validTill).getTime() - new Date().getTime()) / 1000
  );

  return (
    <>
      <Container>
        <Summary>
          <ContentDiv>
            <Image
              src="/dst/congratulations.svg"
              height={12}
              width={115}
              alt="congratulations"
            />
            <HeaderWrapper>
              {' '}
              <StyHeaderTypography variant="h3">{header}</StyHeaderTypography>
            </HeaderWrapper>

            <SubHeader>
              {subHeader}
              {state === DST_STATES.WON && (
                <>
                  &nbsp;•&nbsp;
                  {timeRemaining > 24 * 3600 ? (
                    `Valid till ${getDateMonthString(validTill)}`
                  ) : (
                    <TimeLeft timeRemaining={timeRemaining} />
                  )}
                </>
              )}
            </SubHeader>
            <SubHeaderMweb variant="h6" color="var(--color-text-secondary)">
              {subHeader}
            </SubHeaderMweb>
          </ContentDiv>
          <WebStyImage
            src="/dst/celebration.svg"
            height={84}
            width={68}
            alt="celebration"
          />
          <MWebStyImage
            src="/dst/celebration.svg"
            height={108}
            width={88}
            alt="celebration"
          />
        </Summary>
        <Scholarship
          backgroundImg={getBackgroundImage(discountPercentage)}
          isReattempt={state === DST_STATES.REATTEMPT}
        >
          <ProofMarker />
          <ProofMarker />
          <ProofMarker />
          <ProofMarker />
          <Details>
            <Text>
              {state === DST_STATES.REATTEMPT
                ? 'Your discount code'
                : 'Your scholarship code'}
            </Text>
            <Code>{code}</Code>
          </Details>
          {state === DST_STATES.REATTEMPT && attemptsLeft > 0 && (
            <ReattemptBtn
              label="Reattempt test"
              variant="hollow"
              color="black"
              onClick={clickHandlers.reTakeTest}
            />
          )}
          <StyledBtn
            label={
              state === DST_STATES.REATTEMPT
                ? 'Get discount'
                : 'Get scholarship'
            }
            variant="filled"
            color="black"
            onClick={() => clickHandlers.getScholarship(code)}
          />
        </Scholarship>
        <MwebScholarshipBanner>
          <Details>
            <Text>
              {state === DST_STATES.REATTEMPT
                ? 'Your discount code'
                : 'Your scholarship code'}
            </Text>
          </Details>
          <ScholarshipMweb
            backgroundImg={getMobBackgroundImage(discountPercentage)}
            isReattempt={state === DST_STATES.REATTEMPT}
          >
            <Code>{code}</Code>
          </ScholarshipMweb>
        </MwebScholarshipBanner>
        <MwebActionButtons>
          <StyAquillaButton
            variant="primary"
            size="large"
            fullWidth
            label={label}
            onClick={() => clickHandlers.getScholarship(code)}
          />
          {state === DST_STATES.REATTEMPT && attemptsLeft > 0 && (
            <ReattemptTestContainer onClick={clickHandlers.reTakeTest}>
              REATTEMPT TEST
              <RightChevron height={16} width={16} />
            </ReattemptTestContainer>
          )}
        </MwebActionButtons>
      </Container>
    </>
  );
};

export default Result;
